import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { ApiError, createApiClient } from '@/shared/api/client'
import { holdRedirects } from '@/shared/auth/redirect-hold'
import { SiteSignInOptionsProvider, type SiteSignInOptions } from '@/shared/auth/site-sign-in-options'
import { useRuntimeConfig } from '@/shared/config/runtime-config-context'
import { isAddressHeld, useAddressHeld } from '@/shared/layout/address-hold'
import { SiteNameProvider } from '@/shared/layout/site-name'
import { log, LogLevel } from '@/shared/logging'

import { ActingSiteDisplayContext, type ActingSiteDisplay } from './acting-site-display'
import { loadCurrentSite, lookupDeadline, pageNavigation, primaryAddress, type CurrentSite } from './current-site'
import { CurrentSiteRefreshContext } from './current-site-refresh'
import { SiteBrandingApplied } from './SiteBrandingApplied'

/** Whether the API client logged the failure itself: no answer, or one that was not a 2xx. */
function loggedByClient(error: unknown): boolean {
  return error instanceof ApiError && (error.status < 200 || error.status > 299)
}

/**
 * Loads the current site when the app starts, signed in or not, and gives the shell its name and
 * branding and sign-in its options. On another of the site's base URLs it moves the page to the primary one
 * when the site says so; a page that holds its address (`address-hold`) moves once it lets go.
 * Until the site loads, or if it cannot, the pages show as they are, the shell keeps its own name
 * and sign-in uses the address's own hints. Sign-in and the other MSAL redirects wait for that answer, so none starts on an address the page then leaves.
 */
export function CurrentSiteProvider(props: { readonly children: ReactNode }) {
  const { apiBaseUrl } = useRuntimeConfig()
  // No token: the current site is the same for everyone, and is read before anyone signs in.
  const client = useMemo(() => createApiClient(apiBaseUrl), [apiBaseUrl])
  const [site, setSite] = useState<CurrentSite | null>(null)
  const [resolved, setResolved] = useState(false)
  // The site whose move to its primary address waits for the page to let go of its address.
  const [deferredMove, setDeferredMove] = useState<CurrentSite | null>(null)
  const addressHeld = useAddressHeld()
  // Numbers each read of the site, the first and every one after a save. An answer sets the site
  // only when no newer read has set it already, so a late older answer never replaces a newer one,
  // and a newer read that fails or is not taken leaves an older answer free to land.
  const reads = useRef(0)
  const applied = useRef(0)
  const shown = useRef<CurrentSite | null>(null)
  const take = useCallback((read: number, loaded: CurrentSite) => {
    applied.current = read
    shown.current = loaded
    setSite(loaded)
  }, [])

  // A layout effect, so the hold is in place before any effect below this provider asks for a token.
  useLayoutEffect(() => {
    reads.current += 1
    const read = reads.current
    const controller = new AbortController()
    const release = holdRedirects()
    // A lookup that never answers would hold sign-in for good, so it gives up after a deadline.
    const lookup = new AbortController()
    let timedOut = false
    const deadline = setTimeout(() => {
      timedOut = true
      lookup.abort()
    }, lookupDeadline.ms)
    const load = async () => {
      let loaded: CurrentSite
      try {
        loaded = await loadCurrentSite(client, lookup.signal)
      } catch (error: unknown) {
        clearTimeout(deadline)
        // A lookup this effect abandoned, as StrictMode's replay does, decides nothing: the replay will.
        if (controller.signal.aborted) {
          return
        }
        if (timedOut) {
          log(LogLevel.Warning, 'The current site did not answer in time.', { deadlineMs: lookupDeadline.ms })
        } else if (!loggedByClient(error)) {
          // The API client logs a failed call itself; a 2xx answer it could not read, or of the wrong shape, is logged here.
          log(
            LogLevel.Error,
            'The current site could not be read.',
            error instanceof Error ? error : { error: String(error) },
          )
        }
        setResolved(true)
        release()
        return
      }
      clearTimeout(deadline)
      if (controller.signal.aborted) {
        return
      }
      const primary = primaryAddress(loaded, window.location)
      // A newer site already taken, as a save left it, decides whether the page moves; this answer does not.
      if (primary === null || read < applied.current) {
        if (read > applied.current) {
          take(read, loaded)
        }
        setResolved(true)
        release()
      } else if (isAddressHeld()) {
        // Shown with the site's branding; the move waits until the page lets go of its address,
        // and redirects stay held until then, so no sign-in starts on this address.
        take(read, loaded)
        setDeferredMove(loaded)
      } else {
        // Redirects stay held: the page is leaving.
        pageNavigation.replace(primary)
      }
    }
    void load()
    return () => {
      clearTimeout(deadline)
      controller.abort()
      lookup.abort()
      release()
    }
  }, [client, take])

  // The put-off move, made once the page lets go of its address, as when the person leaves it.
  useEffect(() => {
    if (addressHeld || deferredMove === null) {
      return
    }
    const primary = primaryAddress(deferredMove, window.location)
    if (primary !== null) {
      pageNavigation.replace(primary)
    }
  }, [addressHeld, deferredMove])

  // After a write to a site: takes the site as it is now, if it is still the page's own, or if the
  // first read failed and this address needs no move to the primary. Each read cancels the
  // refresh before it.
  const refreshing = useRef<AbortController | null>(null)
  // Reads the site a Super User acts on again, through their session; set while they act on one.
  const actingRefresher = useRef<(() => Promise<void>) | null>(null)
  const refresh = useCallback(async () => {
    void actingRefresher.current?.()
    refreshing.current?.abort()
    const controller = new AbortController()
    refreshing.current = controller
    reads.current += 1
    const read = reads.current
    try {
      const loaded = await loadCurrentSite(client, controller.signal)
      const current = shown.current
      const fits = current === null ? primaryAddress(loaded, window.location) === null : current.id === loaded.id
      if (!controller.signal.aborted && read > applied.current && fits) {
        take(read, loaded)
      }
    } catch {
      // The page keeps the site it shows; the API client logged a failed call.
    }
  }, [client, take])

  // The site a Super User acts on, shown in place of the address's own; sign-in stays the address's.
  const [acting, setActing] = useState<CurrentSite | null>(null)
  const shownSite = acting ?? site
  const actingDisplay = useMemo<ActingSiteDisplay>(
    () => ({
      show: setActing,
      setRefresher: (refresher) => {
        actingRefresher.current = refresher
      },
    }),
    [],
  )

  const signInOptions = useMemo<SiteSignInOptions | null>(
    () =>
      site === null
        ? null
        : {
            domainHint: site.signInOptions?.domainHint ?? null,
            customB2CPolicy: site.signInOptions?.customB2CPolicy ?? null,
          },
    [site],
  )

  return (
    <SiteNameProvider name={shownSite?.name ?? null}>
      <SiteBrandingApplied site={shownSite}>
        <CurrentSiteRefreshContext value={refresh}>
          <ActingSiteDisplayContext value={actingDisplay}>
            <SiteSignInOptionsProvider options={signInOptions} resolved={resolved}>{props.children}</SiteSignInOptionsProvider>
          </ActingSiteDisplayContext>
        </CurrentSiteRefreshContext>
      </SiteBrandingApplied>
    </SiteNameProvider>
  )
}
